import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { CbmImpl, TaskParticipant } from "~/modules/cbm";
import { LoggerName, getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { getReservationsByTask, sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { FlexSdkError, ErrorCode, ErrorSeverity } from "~/modules/error";
import { Action } from "~/modules/actions/Action";




















export class KickParticipant implements Action<Promise<void>> {
    


    #targetParticipantSid: string;

    


    #taskSid: string;

    constructor(targetParticipantSid: string, taskSid: string) {
        this.#targetParticipantSid = targetParticipantSid;
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`kickParticipant invoked with #taskSid: ${this.#taskSid}`);

        const isWorkerSid = this.#targetParticipantSid.startsWith("WK");
        const isParticipantSid = this.#targetParticipantSid.startsWith("UT");
        if (!isWorkerSid && !isParticipantSid) {
            const errorMsg = `kickParticipant: Invalid targetParticipantSid ${
                this.#targetParticipantSid
            }, either worker sid or participant sid should be provided`;
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!worker) {
            const errorMsg = "kickParticipant: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations?.find((res) => res.status === "accepted");
        if (!reservation) {
            const errorMsg = `kickParticipant: Reservation for task ${this.#taskSid} not found`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.KickParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const task = reservation.task;

        let participants: TaskParticipant[] = [];
        try {
            participants = await cbm.getParticipantsByTask(task);
        } catch (e) {
            const errorMsg = `kickParticipant: Failed to get participants for task ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.KickParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const participant = participants.find(
            (p) =>
                p.routingProperties?.workerSid === this.#targetParticipantSid ||
                p.participantSid === this.#targetParticipantSid
        );
        if (!participant) {
            const errorMsg = `kickParticipant: Participant with sid ${this.#targetParticipantSid} not found for task ${
                this.#taskSid
            }`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.KickParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (worker.sid === participant.routingProperties?.workerSid) {
            const errorMsg = `kickParticipant: Worker ${worker.sid} cannot kick self from task ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.KickParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            await cbm.removeVoiceParticipant(task, this.#targetParticipantSid);
            sendTrackingEvent(EVENTS.KickParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            return Promise.resolve();
        } catch (e) {
            const errorMsg = `kickParticipant: Failed to kick participant ${
                this.#targetParticipantSid
            } from reservation ${reservation.sid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.KickParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
